export class TimeUtils {
  // HH:MM 형식으로 포맷 (분 단위)
  static formatTime(minutes: number): string {
    const hours = Math.floor(minutes / 60)
    const mins = minutes % 60
    return `${hours.toString().padStart(2, "0")}:${mins.toString().padStart(2, "0")}`
  }

  // HH:MM:SS 형식으로 포맷 (초 단위)
  static formatTimeWithSeconds(seconds: number): string {
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    const secs = seconds % 60
    return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`
  }

  // HH:MM:SS.ms 형식으로 포맷 (100ms 단위)
  static formatTimeWithMilliseconds(hundredMs: number): string {
    const totalSeconds = hundredMs / 10 // 100ms 단위를 초로 변환
    const hours = Math.floor(totalSeconds / 3600)
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    const seconds = Math.floor(totalSeconds % 60)
    const tenthSecond = hundredMs % 10 // 100ms 단위 (0-9)

    return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}.${tenthSecond}`
  }

  // 범용 포맷 함수 (format 타입에 따라)
  static format(value: number, format: "HH:MM" | "HH:MM:SS" | "HH:MM:SS.ms" = "HH:MM"): string {
    if (format === "HH:MM:SS.ms") {
      return TimeUtils.formatTimeWithMilliseconds(value)
    }
    if (format === "HH:MM:SS") {
      return TimeUtils.formatTimeWithSeconds(value)
    }
    return TimeUtils.formatTime(value)
  }

  // HH:MM 형식의 문자열을 분으로 변환
  static parseTime(timeString: string): number | null {
    const cleaned = timeString.replace(/[^\d:]/g, "")
    const parts = cleaned.split(":")

    if (parts.length !== 2) return null

    const hours = parseInt(parts[0], 10)
    const minutes = parseInt(parts[1], 10)

    if (isNaN(hours) || isNaN(minutes) || hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
      return null
    }

    return hours * 60 + minutes
  }

  // HH:MM:SS 형식의 문자열을 초로 변환
  static parseTimeWithSeconds(timeString: string): number | null {
    const cleaned = timeString.replace(/[^\d:]/g, "")
    const parts = cleaned.split(":")

    if (parts.length !== 3) return null

    const hours = parseInt(parts[0], 10)
    const minutes = parseInt(parts[1], 10)
    const seconds = parseInt(parts[2], 10)

    if (
      isNaN(hours) ||
      isNaN(minutes) ||
      isNaN(seconds) ||
      hours < 0 ||
      hours > 23 ||
      minutes < 0 ||
      minutes > 59 ||
      seconds < 0 ||
      seconds > 59
    ) {
      return null
    }

    return hours * 3600 + minutes * 60 + seconds
  }

  // HH:MM:SS.ms 형식의 문자열을 100ms 단위로 변환
  static parseTimeWithMilliseconds(timeString: string): number | null {
    const cleaned = timeString.replace(/[^\d:.]/g, "")
    const [timePart, msPart] = cleaned.split(".")
    const parts = timePart.split(":")

    if (parts.length !== 3) return null

    const hours = parseInt(parts[0], 10)
    const minutes = parseInt(parts[1], 10)
    const seconds = parseInt(parts[2], 10)
    const tenthSecond = msPart ? parseInt(msPart.substring(0, 1), 10) : 0 // 소수점 첫 자리만

    if (
      isNaN(hours) ||
      isNaN(minutes) ||
      isNaN(seconds) ||
      isNaN(tenthSecond) ||
      hours < 0 ||
      hours > 23 ||
      minutes < 0 ||
      minutes > 59 ||
      seconds < 0 ||
      seconds > 59 ||
      tenthSecond < 0 ||
      tenthSecond > 9
    ) {
      return null
    }

    // 100ms 단위로 반환
    return (hours * 3600 + minutes * 60 + seconds) * 10 + tenthSecond
  }

  // 범용 파싱 함수 (format 타입에 따라)
  static parse(
    timeString: string,
    format: "HH:MM" | "HH:MM:SS" | "HH:MM:SS.ms" = "HH:MM",
  ): number | null {
    if (format === "HH:MM:SS.ms") {
      return TimeUtils.parseTimeWithMilliseconds(timeString)
    }
    if (format === "HH:MM:SS") {
      return TimeUtils.parseTimeWithSeconds(timeString)
    }
    return TimeUtils.parseTime(timeString)
  }

  // 총 시간(분)을 시간과 분으로 분리
  static getDurationParts(totalMinutes: number): { hours: number; minutes: number } {
    return {
      hours: Math.floor(totalMinutes / 60),
      minutes: totalMinutes % 60,
    }
  }

  // 분을 초로 변환
  static minutesToSeconds(minutes: number): number {
    return minutes * 60
  }

  // 초를 분으로 변환
  static secondsToMinutes(seconds: number): number {
    return Math.floor(seconds / 60)
  }
}
